import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { supabaseMarketData, isMarketDataConfigured } from '@/lib/supabase-market-data'
import { captureError } from '@/lib/sentry'
import { fetchAllRows } from '@/lib/supabase-paginate'
import { fetchDbcConfig } from '@/lib/dbcConfig'
import type { TimePeriod } from '@/types/dashboard'
import { periodToMs } from '@/types/dashboard'
import type { DashboardTimeSeries } from '@/types/timeseries'
import { getBucketConfig, createEmptyBuckets, getBucketIndex } from '@/types/timeseries'
import { DEXTORO_EFFECTIVE_RATE_GRADUATED } from '@/lib/constants/feeShares'

// Meteora's fixed 20% protocol cut on bonding curve trading fees.
const METEORA_BC_FEE_SHARE = 0.20

// DAMM V2 effective rate: 2% * 80% LP * 30% dextoro share. Centralised in feeShares.ts.
const DAMM_V2_DEXTORO_EFFECTIVE_RATE = DEXTORO_EFFECTIVE_RATE_GRADUATED

export function useDashboardTimeSeries(period: TimePeriod) {
  return useQuery({
    queryKey: ['dashboard-timeseries', period],
    queryFn: async (): Promise<DashboardTimeSeries> => {
      const now = Date.now()
      const periodMs = periodToMs(period)
      const periodStartMs = now - periodMs
      const periodStartDate = new Date(periodStartMs)
      const config = getBucketConfig(period)

      // Fetch on-chain fee config
      let tradingFeeRate = 0.01
      let protocolShareBC = 0.70
      let migrationFeePerGraduationSOL = 0

      try {
        const dbcConfig = await fetchDbcConfig()
        tradingFeeRate = dbcConfig.tradingFeeRate
        protocolShareBC = dbcConfig.protocolTradingShareBC
        migrationFeePerGraduationSOL = dbcConfig.migrationFeePerGraduationSOL
      } catch {
        // Continue with defaults
      }

      // Pre-fill empty buckets for consistent X-axis
      const revenue = createEmptyBuckets(period)
      const launches = createEmptyBuckets(period)
      const volume = createEmptyBuckets(period)
      const activeTokens = createEmptyBuckets(period)

      // 1. Fetch tokens, bucket launches, build graduation map
      const { data: allTokens } = await supabase
        .from('launched_tokens')
        .select('mint_address, pool_address, damm_pool_address, is_migrated, migrated_at, created_at')

      const tokenGraduation = new Map<string, { migratedAt: Date | null }>()
      const poolToMint = new Map<string, string>()

      allTokens?.forEach((token) => {
        const mintAddress = token.mint_address as string
        const isMigrated = token.is_migrated as boolean
        const migratedAtRaw = token.migrated_at as string | null
        const migratedAt = isMigrated && migratedAtRaw ? new Date(migratedAtRaw) : null

        if (mintAddress) {
          tokenGraduation.set(mintAddress, { migratedAt })
          const pool = token.pool_address as string | undefined
          const dammPool = token.damm_pool_address as string | undefined
          if (pool) poolToMint.set(pool, mintAddress)
          if (dammPool) poolToMint.set(dammPool, mintAddress)
        }

        // Bucket token launches
        const createdAtRaw = token.created_at as string | undefined
        if (!createdAtRaw) return
        const createdMs = new Date(createdAtRaw).getTime()
        const idx = getBucketIndex(createdMs, periodStartMs, config.bucketMs, config.bucketCount)
        if (idx >= 0) {
          launches[idx].value += 1
        }

        // Bucket migration fee events into revenue
        if (migratedAt && migrationFeePerGraduationSOL > 0) {
          const migIdx = getBucketIndex(migratedAt.getTime(), periodStartMs, config.bucketMs, config.bucketCount)
          if (migIdx >= 0) {
            revenue[migIdx].value += migrationFeePerGraduationSOL
          }
        }
      })

      // 2. Fetch trades, bucket volume[], revenue[], and activeTokens[]
      if (isMarketDataConfigured()) {
        try {
          // Fetch trades with server-side date filter + pagination.
          // Supabase returns max 1000 rows by default; fetchAllRows paginates to get all.
          const filters = period !== 'all'
            ? [{ column: 'created_at', op: 'gte' as const, value: periodStartDate.toISOString() }]
            : []

          const { data: trades } = await fetchAllRows(supabaseMarketData!, {
            table: 'trades',
            select: 'quote_amount, pool_address, created_at, block_time',
            filters,
          })

          // Track unique pools per bucket for activeTokens
          const poolsPerBucket = new Map<number, Set<string>>()

          trades?.forEach((trade: Record<string, unknown>) => {
            const solAmount = Number(trade.quote_amount || 0)
            if (solAmount <= 0) return

            const createdAtRaw = trade.created_at || trade.block_time
            const tradeDate = createdAtRaw ? new Date(createdAtRaw as string) : new Date()
            const tradeMs = tradeDate.getTime()

            const idx = getBucketIndex(tradeMs, periodStartMs, config.bucketMs, config.bucketCount)
            if (idx < 0) return

            // Bucket volume
            volume[idx].value += solAmount

            // Track active tokens
            const poolAddress = trade.pool_address as string | undefined
            if (poolAddress) {
              if (!poolsPerBucket.has(idx)) poolsPerBucket.set(idx, new Set())
              poolsPerBucket.get(idx)!.add(poolAddress)
            }

            // Determine graduation status via pool address lookup
            const resolvedMint = poolAddress ? poolToMint.get(poolAddress) : undefined
            let isGraduated = false
            if (resolvedMint) {
              const tokenInfo = tokenGraduation.get(resolvedMint)
              isGraduated = !!(tokenInfo?.migratedAt && tradeDate >= tokenInfo.migratedAt)
            }

            // Compute dextoro revenue share based on pool type
            if (isGraduated) {
              // DAMM V2: 2% total fee, 30% dextoro LP, 70% creator LP, 20% Meteora
              revenue[idx].value += solAmount * DAMM_V2_DEXTORO_EFFECTIVE_RATE
            } else {
              // Bonding curve: 1% fee, subtract Meteora's 20%, then apply dextoro's LP share
              const totalFee = solAmount * tradingFeeRate
              const lpShare = 1 - METEORA_BC_FEE_SHARE
              revenue[idx].value += totalFee * lpShare * protocolShareBC
            }
          })

          // Fill activeTokens from poolsPerBucket
          poolsPerBucket.forEach((pools, idx) => {
            activeTokens[idx].value = pools.size
          })
        } catch (error) {
          captureError(error instanceof Error ? error : new Error('useDashboardTimeSeries: market data fetch failed'), {
            feature: 'admin_dashboard',
            component: 'useDashboardTimeSeries',
          })
        }
      }

      return { revenue, launches, volume, activeTokens }
    },
    staleTime: 60_000,
    refetchInterval: 60_000,
  })
}
